// core
import React, { useState } from "react";

// components
import Loader from "../../Loader/Loader";
import Default from "../../../../compare/assets/loader.gif";

// styles
import cx from "classnames";
import styles from "./Button.module.scss";
import DataAttribute from "../../../../common/interfaces/analytics/DataAttribute";
import useIsMounted from "../../../hooks/useIsMounted";

const P_BUTTON = "p";
const A_BUTTON = "a";
const BUTTON = "button";

export type TargetTypes = "_blank" | "_self" | "_parent" | "_top";
export type ButtonSizes = "xl" | "l" | "m" | "s" | "xs";
export type ButtonTypes = "submit" | "button" | "reset";

interface IButton {
    component?: "button" | "p" | "a"; // Default "button"
    size?: ButtonSizes; // Default "m"
    variant?: "calltoaction" | "default" | "select" | "box"; // Default "default"
    width?: "default" | "content" | "fill";
    isDisabled?: boolean;
    isActive?: boolean;
    isSubmitting?: boolean;
    onClick?: () => void | Promise<void>;
    dataAttributes?: DataAttribute;
    className?: string;
}

type ExtraProps = {
    tabIndex: number;
    type?: ButtonTypes;
    href?: string;
    target?: TargetTypes;
};

type PButton = IButton & {
    component: typeof P_BUTTON;
};

type Button = IButton & {
    component: typeof BUTTON;
    type?: ButtonTypes; //Default: "button"
};

type AButton = IButton & {
    component: typeof A_BUTTON;
    href?: string;
    target?: TargetTypes;
};

type ButtonProps = Button | PButton | AButton;

const loadingIcons = {
    Default
};

const Button: React.FC<ButtonProps> = (props) => {
    const { isDisabled, isActive, className, isSubmitting, onClick, children } = props;
    const size = props.size || "m";
    const variant = props.variant || "default";
    const width = props.width || "default";

    const isMounted = useIsMounted();

    const [isProcessing, setIsProcessing] = useState(false);
    const isLoading = isProcessing || isSubmitting;

    const handleClick = async (): Promise<void> => {
        if (!onClick) return;
        setIsProcessing(true);
        try {
            await onClick();
        } catch (err) {
            // We don't want to handle errors in the button, just pass them up.
            throw err;
        } finally {
            isMounted.current && setIsProcessing(false);
        }
    };

    const Component = props.component || "button";
    let extraProps: ExtraProps = { tabIndex: 0 };
    const dataAttributes = props.dataAttributes || {};

    if (props.component === "button") {
        extraProps = { ...extraProps, type: props.type || "button" };
    } else if (props.component === "a") {
        extraProps = { ...extraProps, href: props.href, target: props.target };
    } else {
        extraProps = { ...extraProps, type: "button" };
    }

    if (isDisabled) {
        extraProps = { ...extraProps, tabIndex: -1 };
    }

    return (
        <Component
            onClick={(!isDisabled && !isLoading && handleClick) || undefined}
            className={cx({
                // State Control
                "btn-disabled": isDisabled,
                [styles["button--active"]]: isActive,
                // Style Variants
                "btn-primary": variant === "calltoaction",
                "btn-secondary": variant === "default",
                [styles["button--variant-select"]]: variant === "select",
                [styles["button--variant-box"]]: variant === "box",
                // Width
                [styles["width-default"]]: width === "default",
                [styles["width-fill"]]: width === "fill",
                [styles["width-content"]]: width === "content",

                [className]: !!className,
                // This is needed for this auto handler to work
                ["ga_click"]:
                    dataAttributes["data-ga_category"] &&
                    dataAttributes["data-ga_action"] &&
                    dataAttributes["data-ga_label"]
            })}
            {...extraProps}
            {...dataAttributes}>
            {!isLoading ? children : <img alt="loading" src={loadingIcons["Default"]} />}
        </Component>
    );
};

export default Button;
